import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
import PageLoader from "./PageLoader";
import RichText from "./RichText";
import { SectionHeader } from "./CommunityViews";

/* ---------- helpers ---------- */
const ACCESS = {
    1: { label: "Free", cls: "bg-green-50 text-green-700" },
    2: { label: "Paid", cls: "bg-amber-50 text-amber-700" },
    3: { label: "Locked", cls: "bg-gray-100 text-gray-500" },
};
const LOCK_MSG = {
    paid: "This is a paid course.",
    login: "Sign in to access this course.",
    level: "Reach a higher level to unlock this course.",
};

// "2026-06-20" -> "Jun 20, 2026"
function fmtDripDate(d) {
    if (!d) return "";
    const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
    if (isNaN(dt)) return String(d);
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Lock message — level-gated courses + per-lesson drip schedules.
function lockMsg(reason, ctx = {}) {
    if (reason === "level" && ctx.unlock_level) {
        const pts = ctx.unlock_points ? ` (${ctx.unlock_points} pts)` : "";
        return `Reach ${ctx.unlock_level}${pts} to unlock this course.`;
    }
    if (reason === "drip_prev") return "Complete the previous lesson to unlock this one.";
    if (reason === "drip_days" || reason === "drip_date") {
        return ctx.unlock_on ? `Unlocks on ${fmtDripDate(ctx.unlock_on)}.` : "This lesson unlocks later.";
    }
    return LOCK_MSG[reason] || "This course is locked.";
}

function lessonTypeMeta(t) {
    if (t === 2) return { label: "Audio", color: "#8b5cf6", icon: "M9 19V6l12-2v13M9 19a3 3 0 11-6 0 3 3 0 016 0zm12-3a3 3 0 11-6 0 3 3 0 016 0z" };
    if (t === 3) return { label: "Video", color: "#ef4444", icon: "M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" };
    return { label: "Content", color: "#0ea5e9", icon: "M9 12h6m-6 4h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" };
}

// Circular progress ring used in the course header. `dark` tunes it for use
// over a dark banner (white track + white label).
function ProgressRing({ pct, primary, size = 46, dark = false }) {
    const r = (size - 6) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (Math.min(pct, 100) / 100) * c;
    const color = pct >= 100 ? (dark ? "#4ade80" : "#16a34a") : primary;
    const track = dark ? "rgba(255,255,255,0.25)" : "#e5e7eb";
    return (
        <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth="4" />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .45s ease" }} />
            </svg>
            <span className="absolute text-[10px] font-extrabold" style={{ color: dark ? "#ffffff" : color }}>{pct}%</span>
        </span>
    );
}

function Icon({ d, className = "w-4 h-4" }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d={d} />
        </svg>
    );
}

// Lightweight hover tooltip (no native `title`). Wraps a truncated element and
// shows its full text in a dark bubble on hover. The bubble is a sibling of the
// truncated child so it isn't clipped by the child's overflow-hidden.
function Tooltip({ label, className = "", children }) {
    if (!label) return children;
    return (
        <div className={`group/tt relative ${className}`}>
            {children}
            <span className="pointer-events-none absolute left-0 top-full z-30 mt-1.5 hidden w-max max-w-[16rem] whitespace-normal break-words rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg group-hover/tt:block">
                {label}
            </span>
        </div>
    );
}

// Normalise a YouTube/Vimeo/file URL into an embeddable src.
function embedUrl(raw) {
    let url = (raw || "").trim();
    const iframe = url.match(/src="([^"]+)"/);
    if (iframe) url = iframe[1];
    if (url.includes("youtube.com/watch")) {
        try { const v = new URL(url).searchParams.get("v"); if (v) return `https://www.youtube.com/embed/${v}`; } catch { /* */ }
    } else if (url.includes("youtu.be/")) {
        const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0]; if (id) return `https://www.youtube.com/embed/${id}`;
    } else if (url.includes("vimeo.com/") && !url.includes("player.vimeo")) {
        const id = url.split("vimeo.com/")[1]?.split(/[?&]/)[0]; if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
}
const isFile = (u) => /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(u || "");

/* ================================================================== */
/* Course player (detail): modules + lessons + lesson viewer           */
/* ================================================================== */
// Goal (assignment) panel shown inside a lesson — gates lesson completion.
function GoalPanel({ goal, primary, form, setForm, submitting, onSubmit }) {
    const st = goal.my_status; // null=not submitted, 0=pending, 1=approved, 2=rejected
    const type = goal.submission_type; // 0 action, 1 text, 2 video, 3 audio
    return (
        <div className="mt-5 rounded-xl border-2 border-dashed p-4" style={{ borderColor: `${primary}55`, backgroundColor: `${primary}08` }}>
            <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ backgroundColor: primary }}>
                    <Icon d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: primary }}>Goal to complete this lesson</p>
                    <p className="text-sm font-bold text-gray-900">{goal.title}</p>
                </div>
            </div>
            {goal.short_description && <p className="mt-2 text-sm text-gray-600">{goal.short_description}</p>}
            {goal.description && <RichText html={goal.description} className="mt-1 text-sm leading-relaxed text-gray-600" />}

            {st === 1 ? (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-semibold text-green-700">
                    <Icon d="M5 13l4 4L19 7" className="w-4 h-4" /> Goal approved — lesson complete!
                </div>
            ) : st === 0 ? (
                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    <p className="font-semibold">Submitted — awaiting instructor review.</p>
                    {goal.my_text && <p className="mt-1 whitespace-pre-wrap text-amber-800/80">{goal.my_text}</p>}
                    {goal.my_url && <a href={goal.my_url} target="_blank" rel="noreferrer" className="mt-1 block break-all underline">{goal.my_url}</a>}
                </div>
            ) : (
                <div className="mt-3">
                    {st === 2 && (
                        <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                            <span className="font-semibold">Not approved.</span>{goal.review_note ? ` ${goal.review_note}` : ""} Please revise and resubmit.
                        </div>
                    )}
                    {type === 1 && (
                        <textarea
                            rows={4}
                            value={form.text}
                            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                            placeholder="Write your response…"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                        />
                    )}
                    {(type === 2 || type === 3) && (
                        <input
                            value={form.url}
                            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                            placeholder={type === 2 ? "Paste your video link…" : "Paste your audio link…"}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                        />
                    )}
                    <button
                        onClick={onSubmit}
                        disabled={submitting}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                        style={{ backgroundColor: primary }}
                    >
                        <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" />
                        {submitting ? "Submitting…" : type === 0 ? "Mark goal done" : "Submit goal"}
                    </button>
                    {goal.approval_type === 2 && (
                        <p className="mt-2 text-xs text-gray-400">An instructor will review your submission before the lesson is marked complete.</p>
                    )}
                </div>
            )}
        </div>
    );
}

// Compact group banner shown on the right of the course detail page — also
// reused in the feed right rail so both pages share one design.
export function GroupMiniBanner({ group, className = "min-h-[180px] lg:h-full" }) {
    if (!group) return null;
    const isPrivate = group.access_type === 2;
    const pricing = group.pricing_type || "free";
    const isFree = pricing === "free";
    const chip = "inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm ring-1 ring-white/25";
    return (
        <div className={`relative ${className} overflow-hidden rounded-2xl border border-gray-200 bg-gray-900 shadow-sm`}>
            {group.banner && <img src={group.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />

            {/* name + stats + badges */}
            <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="line-clamp-1 text-lg font-bold text-white drop-shadow">{group.name}</h3>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-700 shadow-sm backdrop-blur">
                        {isPrivate
                            ? <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" /></svg>
                            : <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" /></svg>}
                        {isPrivate ? "Private" : "Public"}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm ${isFree ? "bg-green-600" : "bg-red-600"}`}>
                        {pricing === "free_trial" ? "Trial" : isFree ? "Free" : "Paid"}
                    </span>
                    <span className={chip}>
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" /></svg>
                        {group.member_count ?? 0} {group.member_count === 1 ? "member" : "members"}
                    </span>
                    <span className={chip}>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4M9 8h6M6 3h12a1 1 0 011 1v16a1 1 0 01-1.5.87L15 19.5l-2.5 1.4a1 1 0 01-1 0L9 19.5l-2.5 1.37A1 1 0 015 20V4a1 1 0 011-1z" /></svg>
                        {group.post_count ?? 0} {group.post_count === 1 ? "post" : "posts"}
                    </span>
                </div>
            </div>
        </div>
    );
}

function CoursePlayer({ token, courseToken, primary, member, group, onBack, initialLesson, onLessonChange, onPointsChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null); // lesson object
    // Keep the latest onLessonChange without re-triggering the effect below.
    const onLessonChangeRef = useRef(onLessonChange);
    onLessonChangeRef.current = onLessonChange;
    // Reflect the active lesson in the URL (?lesson=) so it survives refresh.
    useEffect(() => {
        if (active?.lesson_token) onLessonChangeRef.current?.(active.lesson_token);
    }, [active?.lesson_token]);
    const [collapsed, setCollapsed] = useState({}); // accordion: { [moduleIndex]: true }
    const toggleModule = (mi) => setCollapsed((c) => ({ ...c, [mi]: !c[mi] }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${config.COMMUNITY_API_URL}/community-course-detail`, {
                community_token: token,
                course_token: courseToken,
                member_token: member?.auth_token,
                community_member_id: member?.community_member_id,
            });
            if (res.data?.error === 0) {
                setData(res.data);
                const all = res.data.modules.flatMap((m) => m.lessons);
                // Keep the same lesson selected; on first load honour the lesson
                // from the URL (?lesson=), else default to the first lesson.
                setActive((prev) => {
                    const want = prev?.lesson_token || initialLesson;
                    return (want && all.find((l) => l.lesson_token === want)) || all[0] || null;
                });
            }
        } catch {
            toast.error("Could not load course.");
        } finally {
            setLoading(false);
        }
    }, [token, courseToken, member?.auth_token, member?.community_member_id, initialLesson]);
    useEffect(() => { load(); }, [load]);

    const toggleComplete = async (lesson) => {
        if (!member) return toast.info("Sign in to track your progress.");
        const next = lesson.completed ? 0 : 1;
        try {
            await axios.post(`${config.COMMUNITY_API_URL}/community-lesson-complete`, {
                community_token: token,
                member_token: member?.auth_token,
                lesson_token: lesson.lesson_token,
                completed: next,
            });
            await load();
            setActive((a) => (a && a.lesson_token === lesson.lesson_token ? { ...a, completed: !!next } : a));
            // Completing a lesson awards points server-side — refresh the member's
            // standing immediately so the points card updates without a reload.
            onPointsChange?.();
        } catch {
            toast.error("Could not update progress.");
        }
    };

    // Goal submission (text / link / action) — gates lesson completion.
    const [goalForm, setGoalForm] = useState({ text: "", url: "" });
    const [submitting, setSubmitting] = useState(false);
    const submitGoal = async (lesson) => {
        if (!member) return toast.info("Sign in to submit your goal.");
        setSubmitting(true);
        try {
            const res = await axios.post(`${config.COMMUNITY_API_URL}/community-goal-submit`, {
                community_token: token,
                member_token: member?.auth_token,
                lesson_token: lesson.lesson_token,
                text: goalForm.text,
                url: goalForm.url,
            });
            if (res.data?.error === 0) {
                toast.success(res.data.status === 1 ? "Goal approved — lesson complete! 🎉" : "Submitted for review.");
                setGoalForm({ text: "", url: "" });
                await load();
                // An approved goal completes the lesson and awards points — refresh
                // the member's standing so the points card updates without a reload.
                if (res.data.status === 1) onPointsChange?.();
            } else {
                toast.error(res.data?.msg || "Could not submit your goal.");
            }
        } catch (e) {
            toast.error(e.response?.data?.msg || "Could not submit your goal.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <PageLoader embedded label="Loading course…" primary={primary} />;
    if (!data) return null;

    const { course, can_view, lock_reason, unlock_level, unlock_points, modules } = data;
    const lockCtx = { unlock_level, unlock_points };
    const allLessons = modules.flatMap((m) => m.lessons);
    const done = allLessons.filter((l) => l.completed).length;
    const pct = allLessons.length ? Math.round((done / allLessons.length) * 100) : 0;
    const accessColor = course.access_type === 2 ? "#d97706" : "#16a34a";

    // Where the active lesson sits (module name + index) and flat prev/next.
    const flatIdx = active ? allLessons.findIndex((l) => l.lesson_token === active.lesson_token) : -1;
    const nextLesson = flatIdx >= 0 ? allLessons[flatIdx + 1] : null;
    const prevLesson = flatIdx > 0 ? allLessons[flatIdx - 1] : null;
    let activePos = null;
    if (active) {
        for (let mi = 0; mi < modules.length; mi++) {
            const li = modules[mi].lessons.findIndex((l) => l.lesson_token === active.lesson_token);
            if (li >= 0) { activePos = { moduleName: modules[mi].name, lessonIndex: li }; break; }
        }
    }
    const T = active ? lessonTypeMeta(active.lesson_type) : null;

    return (
        // flex-col + order utilities: on mobile the modules/lesson content comes
        // first and the course + group cards drop to the bottom; on desktop the
        // banner row stays on top (lg:order-* restores the natural order).
        <div className="flex flex-col">
            <button onClick={onBack} className="order-1 inline-flex items-center gap-2 self-start text-sm font-semibold text-gray-600 hover:text-gray-900">
                <Icon d="M15 19l-7-7 7-7" /> Back to courses
            </button>

            {/* ---------- Course banner (left) + group banner (right) ---------- */}
            <div className="order-4 mt-4 grid grid-cols-1 gap-5 lg:order-2 lg:grid-cols-12">
              <div className={group ? "lg:col-span-8" : "lg:col-span-12"}>
                {/* Premium split card: clean image panel + content panel, so the
                    uploaded course image is shown fully (no text/gradient overlay). */}
                <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white shadow-sm sm:flex-row">
                    <div className="relative shrink-0 overflow-hidden rounded-t-2xl bg-gray-100 sm:w-[42%] sm:rounded-l-2xl sm:rounded-tr-none lg:w-[44%]">
                        {course.course_image ? (
                            <img src={course.course_image} alt={course.name} className="h-44 w-full object-cover sm:h-full" />
                        ) : (
                            <div className="flex h-44 w-full items-center justify-center bg-gray-900 text-gray-600 sm:h-full"><Icon d="M12 14l9-5-9-5-9 5 9 5z" className="h-10 w-10" /></div>
                        )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ color: accessColor, backgroundColor: `${accessColor}1A` }}>
                                {ACCESS[course.access_type]?.label}{course.access_type === 2 && course.price > 0 ? ` · ${course.price}` : ""}
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-600">
                                <svg className="h-3 w-3 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                                {Number(course.star_rating || 0).toFixed(1)}
                            </span>
                        </div>
                        <Tooltip label={course.name} className="mt-2">
                            <h1 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">{course.name}</h1>
                        </Tooltip>
                        {course.short_description && (
                            <Tooltip label={course.short_description} className="mt-1 max-w-2xl">
                                <p className="text-[13px] text-gray-500 line-clamp-2">{course.short_description}</p>
                            </Tooltip>
                        )}

                        {/* progress + stats */}
                        <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-3">
                            <div className="flex items-center gap-3">
                                <ProgressRing pct={pct} primary={primary} />
                                <div>
                                    <p className="text-sm font-bold text-gray-900">{done} of {allLessons.length} lessons</p>
                                    <p className="text-xs text-gray-500">{pct === 100 ? "Course complete 🎉" : pct > 0 ? "Keep going — you're making progress" : "Not started yet"}</p>
                                </div>
                            </div>
                            <div className="ml-auto flex items-center gap-5 text-sm text-gray-600">
                                <span className="inline-flex items-center gap-1.5 font-semibold">
                                    <Icon d="M4 6h16M4 10h16M4 14h10M4 18h10" className="h-4 w-4 text-gray-400" /> {modules.length} modules
                                </span>
                                <span className="inline-flex items-center gap-1.5 font-semibold">
                                    <Icon d="M12 6.5C10.5 5.5 8.3 5 6 5a8 8 0 00-2 .26v12A8 8 0 016 17c2.3 0 4.5.5 6 1.5m0-12c1.5-1 3.7-1.5 6-1.5a8 8 0 012 .26v12A8 8 0 0018 17c-2.3 0-4.5.5-6 1.5m0-12V18.5" className="h-4 w-4 text-gray-400" /> {allLessons.length} lessons
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
              {group && (
                <div className="lg:col-span-4">
                  <GroupMiniBanner group={group} />
                </div>
              )}
            </div>

            {!can_view && (
                <div className="order-3 mt-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <Icon d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.85-2.74l-7-12a2 2 0 00-3.7 0l-7 12A2 2 0 005 19z" />
                    {lockMsg(lock_reason, lockCtx)}
                </div>
            )}

            <div className="order-2 mt-5 grid grid-cols-1 gap-5 lg:order-4 lg:grid-cols-12">
                {/* ---------- curriculum ---------- */}
                <div className="lg:col-span-5 space-y-4">
                    {modules.map((m, mi) => {
                        const mDone = m.lessons.filter((l) => l.completed).length;
                        const mAll = m.lessons.length;
                        const mComplete = mAll > 0 && mDone === mAll;
                        const isOpen = !collapsed[mi];
                        return (
                            <div key={mi} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                                {/* accordion header — module name opens the first lesson, chevron toggles the module */}
                                <div className="flex items-stretch">
                                    <button
                                        onClick={() => { const first = m.lessons[0]; if (first) setActive(first); }}
                                        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                                    >
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-extrabold"
                                            style={mComplete ? { backgroundColor: "#16a34a", color: "#fff" } : { backgroundColor: `${primary}14`, color: primary }}>
                                            {mComplete ? <Icon d="M5 13l4 4L19 7" className="w-4 h-4" /> : mi + 1}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-bold text-gray-900">{m.name}</p>
                                            <p className="text-[11px] font-medium text-gray-400">{mDone}/{mAll} lessons{mAll > 0 ? ` · ${Math.round((mDone / mAll) * 100)}%` : ""}</p>
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => toggleModule(mi)}
                                        aria-label={isOpen ? "Collapse module" : "Expand module"}
                                        className="flex shrink-0 items-center border-l border-gray-100 px-3.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
                                    >
                                        <Icon d="M19 9l-7 7-7-7" className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                                    </button>
                                </div>
                                {isOpen && (
                                    <div className="divide-y divide-gray-50 border-t border-gray-100">
                                        {m.lessons.length === 0 ? (
                                            <p className="px-4 py-3 text-sm text-gray-400">No lessons.</p>
                                        ) : (
                                            m.lessons.map((l) => {
                                                const LT = lessonTypeMeta(l.lesson_type);
                                                const isActive = active?.lesson_token === l.lesson_token;
                                                const isDrip = l.locked && String(l.lock_reason || "").startsWith("drip");
                                                const locked = !!l.locked;
                                                return (
                                                    <button
                                                        key={l.lesson_token}
                                                        onClick={() => setActive(l)}
                                                        className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${isActive ? "" : locked ? "bg-gray-50/60 hover:bg-gray-100/60" : "hover:bg-gray-50"}`}
                                                        style={isActive ? { backgroundColor: `${primary}0d` } : {}}
                                                    >
                                                        {isActive && <span className="absolute left-0 top-0 h-full w-1 rounded-r" style={{ backgroundColor: primary }} />}
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                                                            style={l.completed
                                                                ? { backgroundColor: primary, color: "#fff" }
                                                                : locked
                                                                    ? { backgroundColor: "#f3f4f6", color: "#9ca3af" }
                                                                    : { backgroundColor: `${LT.color}14`, color: LT.color }}>
                                                            {l.completed
                                                                ? <Icon d="M5 13l4 4L19 7" className="w-4 h-4" />
                                                                : locked
                                                                    ? <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="w-4 h-4" />
                                                                    : <Icon d={LT.icon} className="w-4 h-4" />}
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className={`block truncate text-sm font-semibold ${locked ? "text-gray-400" : isActive ? "text-gray-900" : "text-gray-800"}`}>{l.title}</span>
                                                            {isDrip ? (
                                                                <span className="text-[11px] font-medium" style={{ color: primary }}>
                                                                    {l.lock_reason === "drip_prev"
                                                                        ? "Complete previous to unlock"
                                                                        : l.unlock_on ? `Unlocks ${fmtDripDate(l.unlock_on)}` : "Scheduled"}
                                                                </span>
                                                            ) : locked ? (
                                                                <span className="text-[11px] font-medium text-gray-400">Locked</span>
                                                            ) : (
                                                                <span className="text-[11px] font-medium" style={{ color: LT.color }}>{LT.label}</span>
                                                            )}
                                                        </span>

                                                        {/* indicators: goal · drip · status */}
                                                        <span className="flex shrink-0 items-center gap-1.5">
                                                            {l.has_goal && (
                                                                <span title="Has a goal to complete" className="flex h-6 w-6 items-center justify-center rounded-md" style={{ backgroundColor: "#f59e0b1f", color: "#d97706" }}>
                                                                    <Icon d="M3 3v18M3 4.5h12l-2 3.5 2 3.5H3" className="w-3.5 h-3.5" />
                                                                </span>
                                                            )}
                                                            {l.drip_type > 0 && (
                                                                <span title="Drip-scheduled lesson" className="flex h-6 w-6 items-center justify-center rounded-md" style={{ backgroundColor: "#0ea5e91f", color: "#0284c7" }}>
                                                                    <Icon d="M12 3C9 7 6.5 9.8 6.5 13a5.5 5.5 0 0011 0C17.5 9.8 15 7 12 3z" className="w-3.5 h-3.5" />
                                                                </span>
                                                            )}
                                                            {l.completed && !isActive ? (
                                                                <Icon d="M5 13l4 4L19 7" className="w-4 h-4 text-green-500" />
                                                            ) : locked ? (
                                                                isDrip
                                                                    ? <Icon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="w-4 h-4 text-gray-400" />
                                                                    : <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="w-4 h-4 text-gray-400" />
                                                            ) : null}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ---------- viewer ---------- */}
                <div className="lg:col-span-7">
                    {!active ? (
                        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
                            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                                <Icon d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" className="w-6 h-6" />
                            </span>
                            <p className="mt-3 text-sm font-medium text-gray-400">Select a lesson to begin.</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            {/* media */}
                            {active.locked ? (
                                <div className="flex h-56 flex-col items-center justify-center bg-gray-50 text-gray-400">
                                    <Icon d="M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" className="w-8 h-8" />
                                    <p className="mt-2 text-sm">{lockMsg(active.lock_reason || lock_reason, { ...lockCtx, unlock_on: active.unlock_on })}</p>
                                </div>
                            ) : active.lesson_type === 3 && active.media_url ? (
                                isFile(active.media_url) ? (
                                    <video src={active.media_url} controls className="w-full aspect-video bg-black" />
                                ) : (
                                    <div className="aspect-video bg-black">
                                        <iframe title={active.title} src={embedUrl(active.media_url)} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                                    </div>
                                )
                            ) : active.lesson_type === 2 && active.media_url ? (
                                <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-5" style={{ background: `linear-gradient(135deg, ${T.color}12, #ffffff 70%)` }}>
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ backgroundColor: T.color }}>
                                        <Icon d={T.icon} className="w-6 h-6" />
                                    </span>
                                    <audio src={active.media_url} controls className="w-full" />
                                </div>
                            ) : null}

                            <div className="p-5 sm:p-6">
                                {/* lesson position */}
                                {activePos && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[11px] font-medium text-gray-400">Lesson {activePos.lessonIndex + 1} · {activePos.moduleName}</span>
                                    </div>
                                )}

                                <div className="mt-2 flex items-start justify-between gap-3">
                                    <h2 className="text-xl font-bold text-gray-900">{active.title}</h2>
                                    {active.completed ? (
                                        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-green-700 ring-1 ring-green-200" style={{ backgroundColor: "#f0fdf4" }}>
                                            <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> Completed
                                        </span>
                                    ) : (!active.locked && !active.goal) ? (
                                        <button
                                            onClick={() => toggleComplete(active)}
                                            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110"
                                            style={{ backgroundColor: primary }}
                                        >
                                            <Icon d="M5 13l4 4L19 7" className="w-3.5 h-3.5" /> Mark complete
                                        </button>
                                    ) : null}
                                </div>
                                {active.short_description && <p className="mt-1.5 text-sm text-gray-500">{active.short_description}</p>}

                                {active.locked ? (
                                    <p className="mt-4 text-sm text-gray-400">{lockMsg(active.lock_reason || lock_reason, { ...lockCtx, unlock_on: active.unlock_on })}</p>
                                ) : active.detailed_description ? (
                                    <RichText html={active.detailed_description} className="mt-4 text-sm leading-relaxed text-gray-700" />
                                ) : (
                                    <p className="mt-4 text-sm text-gray-400">No content for this lesson.</p>
                                )}

                                {!active.locked && active.goal && (
                                    <GoalPanel
                                        goal={active.goal}
                                        primary={primary}
                                        form={goalForm}
                                        setForm={setGoalForm}
                                        submitting={submitting}
                                        onSubmit={() => submitGoal(active)}
                                    />
                                )}

                                {/* prev / next navigation */}
                                {(prevLesson || nextLesson) && (
                                    <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                                        {prevLesson ? (
                                            <button onClick={() => setActive(prevLesson)} className="group inline-flex max-w-[45%] items-center gap-2 text-left text-sm font-semibold text-gray-600 hover:text-gray-900">
                                                <Icon d="M15 19l-7-7 7-7" className="w-4 h-4 shrink-0" />
                                                <span className="min-w-0"><span className="block text-[10px] uppercase tracking-wide text-gray-400">Previous</span><span className="block truncate">{prevLesson.title}</span></span>
                                            </button>
                                        ) : <span />}
                                        {nextLesson ? (
                                            <button onClick={() => setActive(nextLesson)} className="group inline-flex max-w-[45%] items-center gap-2 rounded-lg px-3 py-2 text-right text-sm font-bold text-white shadow-sm transition hover:brightness-110" style={{ backgroundColor: primary }}>
                                                <span className="min-w-0"><span className="block truncate">Next Lesson</span></span>
                                                <Icon d="M9 5l7 7-7 7" className="w-4 h-4 shrink-0" />
                                            </button>
                                        ) : <span />}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ================================================================== */
/* Course landing page (shown before entering the course)              */
/* ================================================================== */
function CourseLanding({ token, courseToken, primary, member, onStart, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.post(`${config.COMMUNITY_API_URL}/community-course-detail`, {
                    community_token: token,
                    course_token: courseToken,
                    member_token: member?.auth_token,
                    community_member_id: member?.community_member_id,
                });
                if (active && res.data?.error === 0) setData(res.data);
            } catch {
                toast.error("Could not load course.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token, courseToken, member?.auth_token, member?.community_member_id]);

    const Back = () => (
        <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-800">
            <Icon d="M15 19l-7-7 7-7" className="h-4 w-4" /> Back to courses
        </button>
    );

    if (loading) return <div className="max-w-5xl mx-auto"><Back /><PageLoader embedded label="Loading course…" primary={primary} /></div>;
    if (!data?.course) return <div className="max-w-5xl mx-auto"><Back /><p className="py-16 text-center text-sm text-gray-400">Course not found.</p></div>;

    const c = data.course;
    const landing = data.landing || {};
    const canView = !!data.can_view;
    const total = c.total_lessons || 0;
    const done = c.completed_lessons || 0;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const accessLabel = c.access_type === 3 ? (canView ? "Unlocked" : "Locked") : ACCESS[c.access_type]?.label;
    const accessColor = c.access_type === 2 ? "#d97706" : (c.access_type === 3 && !canView) ? "#6b7280" : "#16a34a";
    const aboutHtml = landing.description || c.description || "";

    const ctaLabel = !canView
        ? (data.lock_reason === "login" ? "Sign in to start"
            : data.lock_reason === "paid" ? `Unlock${c.price > 0 ? ` · ${c.price}` : ""}`
            : "Locked")
        : (total > 0 && done >= total ? "Review course"
            : done > 0 ? "Continue learning"
            : "Start course");

    return (
        <div className="max-w-5xl mx-auto">
            <Back />
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                {/* hero */}
                <div className="relative h-56 w-full bg-gray-900 sm:h-64">
                    {c.course_image ? (
                        <img src={c.course_image} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300"><Icon d="M12 14l9-5-9-5-9 5 9 5z" className="h-12 w-12" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                        <span className="inline-flex items-center rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-bold shadow-sm" style={{ color: accessColor }}>
                            {accessLabel}{c.access_type === 2 && c.price > 0 ? ` · ${c.price}` : ""}
                        </span>
                        <h1 className="mt-2 text-2xl font-bold text-white sm:text-3xl">{c.name}</h1>
                        {c.short_description && <p className="mt-1.5 max-w-2xl text-sm text-white/85">{c.short_description}</p>}
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-white/80">
                            {data.instructor?.name && <span>{data.instructor.name}</span>}
                            {total > 0 && <span>{total} lesson{total === 1 ? "" : "s"}</span>}
                            {c.star_rating > 0 && (
                                <span className="inline-flex items-center gap-1">
                                    <svg className="h-3.5 w-3.5 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                                    {Number(c.star_rating).toFixed(1)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-7 p-5 sm:p-6">
                    {/* CTA + progress */}
                    <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                            {canView && total > 0 ? (
                                <>
                                    <div className="flex items-center justify-between gap-3 text-xs font-semibold">
                                        <span style={{ color: done >= total ? "#16a34a" : primary }}>
                                            {done >= total ? "Completed ✓" : pct > 0 ? `${pct}% complete` : "Not started yet"}
                                        </span>
                                        <span className="text-gray-400">{done}/{total} lessons</span>
                                    </div>
                                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200 sm:w-64">
                                        <div className="h-full rounded-full" style={{ width: `${Math.max(pct, pct > 0 ? 6 : 0)}%`, background: done >= total ? "#16a34a" : primary }} />
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-500">{lockMsg(data.lock_reason, data)}</p>
                            )}
                        </div>
                        <button
                            onClick={() => onStart(canView)}
                            className="shrink-0 rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                            style={{ background: canView ? primary : "#6b7280" }}
                        >
                            {ctaLabel}
                        </button>
                    </div>

                    {/* about */}
                    {aboutHtml && (
                        <div>
                            <h2 className="mb-2 text-lg font-bold text-gray-900">About this course</h2>
                            <div className="prose prose-sm max-w-none text-gray-600"><RichText html={aboutHtml} /></div>
                        </div>
                    )}

                    {/* what you'll learn */}
                    {Array.isArray(landing.skill_list) && landing.skill_list.length > 0 && (
                        <div>
                            <h2 className="mb-3 text-lg font-bold text-gray-900">{landing.skill_heading || "What you'll learn"}</h2>
                            <div className="grid gap-2.5 sm:grid-cols-2">
                                {landing.skill_list.map((s, i) => (
                                    <div key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white" style={{ background: primary }}>
                                            <Icon d="M5 13l4 4L19 7" className="h-3 w-3" />
                                        </span>
                                        <span>{s}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* curriculum */}
                    {Array.isArray(data.modules) && data.modules.length > 0 && (
                        <div>
                            <h2 className="mb-3 text-lg font-bold text-gray-900">Course content</h2>
                            <div className="space-y-3">
                                {data.modules.map((m, mi) => (
                                    <div key={mi} className="overflow-hidden rounded-xl border border-gray-200">
                                        <div className="flex items-center justify-between gap-2 bg-gray-50 px-4 py-2.5">
                                            <span className="text-sm font-semibold text-gray-800">{m.name}</span>
                                            <span className="text-xs text-gray-400">{m.lessons.length} lesson{m.lessons.length === 1 ? "" : "s"}</span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {m.lessons.map((l, li) => (
                                                <div key={li} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                                                    <Icon
                                                        d={l.locked ? "M12 11V7a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6a1 1 0 011-1z" : "M8 5v14l11-7z"}
                                                        className={`h-4 w-4 shrink-0 ${l.locked ? "text-gray-300" : "text-gray-400"}`}
                                                    />
                                                    <span className={`truncate ${l.locked ? "text-gray-400" : "text-gray-700"}`}>{l.title}</span>
                                                    {l.completed && <span className="ml-auto text-xs font-semibold text-green-600">Done</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ================================================================== */
/* Courses list                                                        */
/* ================================================================== */
export default function CoursesView({ token, groupToken, primary, member, group, groupCard, pointsCard, onPointsChange, template = "classic" }) {
    // Per-template course-card design (content restyle, not just nav).
    const variant = COURSE_VARIANT[template] || "default";
    const [courses, setCourses] = useState(null);
    const [loading, setLoading] = useState(true);
    // Deep-link: ?course=<token> opens that course straight away (used by the
    // admin course-landing "Visit Page" button).
    // Course / lesson live in the URL so the page is shareable + survives refresh:
    //   ?course=<token>                       -> course landing page
    //   ?course=<token>&view=lessons&lesson=… -> inside the lessons (player)
    const [sp, setSp] = useSearchParams();
    const open = sp.get("course") || null; // course_token
    const view = sp.get("view") === "lessons" ? "player" : "landing";
    const lessonToken = sp.get("lesson") || null;

    // Update course/lesson params while preserving gid / page / ref etc.
    const setCourseParams = (changes, opts) => {
        setSp((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(changes).forEach(([k, v]) => {
                if (v == null || v === "") next.delete(k);
                else next.set(k, v);
            });
            return next;
        }, opts);
    };

    // Courses the member has already started — once started, we skip the landing
    // and go straight to the lessons. Backed by their progress (cross-device) plus
    // a localStorage flag for the "clicked Start, no progress yet" case.
    const startedKey = `coursesStarted:${token}`;
    const [startedSet, setStartedSet] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(startedKey) || "[]")); }
        catch { return new Set(); }
    });
    const markStarted = (courseToken) => {
        if (!courseToken) return;
        setStartedSet((prev) => {
            const next = new Set(prev);
            next.add(courseToken);
            try { localStorage.setItem(startedKey, JSON.stringify([...next])); } catch { /* ignore */ }
            return next;
        });
    };
    // Has this course been started before? (accessible + progress or local flag)
    const hasStarted = (c) =>
        !c.locked && ((c.completed_lessons || 0) > 0 || (c.progress_pct || 0) > 0 || startedSet.has(c.course_token));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${config.COMMUNITY_API_URL}/community-courses`, {
                community_token: token,
                group_token: groupToken,
                member_token: member?.auth_token,
                community_member_id: member?.community_member_id,
            });
            setCourses(res.data?.error === 0 ? res.data.courses : []);
        } catch {
            setCourses([]);
        } finally {
            setLoading(false);
        }
    }, [token, groupToken, member?.auth_token, member?.community_member_id]);
    useEffect(() => { load(); }, [load]);

    const closeCourse = () => { setCourseParams({ course: null, view: null, lesson: null }); load(); };

    if (open) {
        // Course landing page first; "Start course" reveals the lesson player.
        if (view === "player") {
            // Back from the lessons goes to the course list (the landing is only an
            // intro for not-yet-started courses, so we don't route back through it).
            return (
                <CoursePlayer
                    token={token}
                    courseToken={open}
                    primary={primary}
                    member={member}
                    group={group}
                    initialLesson={lessonToken}
                    onLessonChange={(lt) => setCourseParams({ lesson: lt }, { replace: true })}
                    onPointsChange={onPointsChange}
                    onBack={closeCourse}
                />
            );
        }
        return (
            <CourseLanding
                token={token}
                courseToken={open}
                primary={primary}
                member={member}
                onStart={(canView) => { if (canView) markStarted(open); setCourseParams({ view: "lessons" }); }}
                onBack={closeCourse}
            />
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 order-1">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <SectionHeader
                primary={primary}
                iconPath="M12 6.5C10.5 5.5 8.3 5 6 5a8 8 0 00-2 .26v12A8 8 0 016 17c2.3 0 4.5.5 6 1.5m0-12c1.5-1 3.7-1.5 6-1.5a8 8 0 012 .26v12A8 8 0 0018 17c-2.3 0-4.5.5-6 1.5m0-12V18.5"
                title="Courses"
                subtitle="Courses in this group"
                trailing={courses == null ? "…" : `${courses.length} ${courses.length === 1 ? "course" : "courses"}`}
            />
            <div className="p-5 sm:p-6">
            {loading ? (
                <PageLoader embedded label="Loading courses…" primary={primary} />
            ) : !courses || courses.length === 0 ? (
                <p className="text-center text-gray-400 py-10 text-sm">No courses in this group yet.</p>
            ) : (
                <div className={COURSE_GRID[variant]}>
                    {courses.map((c) => (
                        <CourseCard
                            key={c.course_token}
                            c={c}
                            primary={primary}
                            variant={variant}
                            onClick={() => setCourseParams({ course: c.course_token, view: hasStarted(c) ? "lessons" : null, lesson: null })}
                        />
                    ))}
                </div>
            )}
            </div>
            </div>
          </div>
          {(groupCard || pointsCard) && (
            <aside className="lg:col-span-4 space-y-4 order-2 lg:sticky lg:top-6 lg:self-start">
              {groupCard}
              {pointsCard}
            </aside>
          )}
        </div>
    );
}

// Maps a template to a course-card visual variant + the grid that lays the
// cards out. classic/studio share the standard card; catalog is a denser
// metadata-forward grid; campus uses progress-forward horizontal rows; creator
// uses large single-column feature cards.
const COURSE_VARIANT = {
    classic: "default",
    studio: "library",
    catalog: "compact",
    campus: "row",
    creator: "feature",
};
const COURSE_GRID = {
    default: "grid grid-cols-1 sm:grid-cols-2 gap-5",
    compact: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
    row: "flex flex-col gap-4",
    feature: "grid grid-cols-1 gap-5",
    library: "grid grid-cols-1 xl:grid-cols-2 gap-6",
};

function CourseCard({ c, primary, variant = "default", onClick }) {
    const accessLabel = c.access_type === 3 ? (c.locked ? "Locked" : "Unlocked") : ACCESS[c.access_type]?.label;
    const accessColor = c.access_type === 2 ? "#d97706" : (c.access_type === 3 && c.locked) ? "#6b7280" : "#16a34a";
    const imgH = variant === "feature" ? "h-52" : variant === "library" ? "h-48" : variant === "compact" ? "h-36" : "h-40";

    const accessBadge = (
        <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-bold shadow-sm backdrop-blur" style={{ color: accessColor }}>
            {accessLabel}{c.access_type === 2 && c.price > 0 ? ` · ${c.price}` : ""}
        </span>
    );
    const ratingChip = (
        <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-bold text-amber-500 shadow-sm backdrop-blur">
            <svg className="h-3 w-3 fill-amber-400" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
            {Number(c.star_rating || 0).toFixed(1)}
        </span>
    );
    const lessonsChip = c.total_lessons > 0 && (
        <span className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
            <Icon d="M4 6h16M4 12h10M4 18h7" className="h-3 w-3" />
            {c.total_lessons} lessons
        </span>
    );
    const image = (
        <div className={`relative ${imgH} w-full overflow-hidden bg-gray-900`}>
            {c.course_image ? (
                <img src={c.course_image} alt="" className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />
            ) : (
                <div className="flex h-full w-full items-center justify-center text-gray-300"><Icon d="M12 14l9-5-9-5-9 5 9 5z" className="w-10 h-10" /></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
            {accessBadge}
            {ratingChip}
            {lessonsChip}
            <span className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/95 shadow-lg" style={{ color: primary }}>
                    <Icon d="M8 5v14l11-7z" className="h-6 w-6" />
                </span>
            </span>
        </div>
    );

    const progress = c.total_lessons > 0 ? (
        <>
            <div className="flex items-center justify-between text-[11px] font-bold">
                <span style={{ color: c.completed ? "#16a34a" : primary }}>
                    {c.completed ? "Completed ✓" : c.progress_pct > 0 ? `${c.progress_pct}% complete` : "Start course"}
                </span>
                <span className="text-gray-400">{c.completed_lessons}/{c.total_lessons} lessons</span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${Math.max(c.progress_pct, c.progress_pct > 0 ? 6 : 0)}%`, background: c.completed ? "#16a34a" : `linear-gradient(90deg, ${primary} 0%, ${primary}aa 100%)` }}
                />
            </div>
        </>
    ) : (
        <p className="text-[11px] font-medium text-gray-400">No lessons yet</p>
    );

    // Campus: progress-forward horizontal row (thumbnail left, content + progress right).
    if (variant === "row") {
        return (
            <button
                onClick={onClick}
                className="group flex overflow-hidden rounded-[10px] border border-gray-200 bg-white text-left shadow-sm transition hover:border-gray-300 hover:shadow-md"
            >
                <div className="relative w-36 shrink-0 overflow-hidden bg-gray-900 sm:w-52">
                    {c.course_image ? (
                        <img src={c.course_image} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300"><Icon d="M12 14l9-5-9-5-9 5 9 5z" className="w-8 h-8" /></div>
                    )}
                    {accessBadge}
                </div>
                <div className="flex flex-1 flex-col p-4">
                    <h3 className="line-clamp-1 text-[15px] font-bold text-gray-900">{c.name}</h3>
                    {c.short_description && <p className="mt-1 line-clamp-1 text-[13px] leading-relaxed text-gray-500 sm:line-clamp-2">{c.short_description}</p>}
                    <div className="mt-auto pt-3">{progress}</div>
                </div>
            </button>
        );
    }

    // default / compact / feature / library: vertical card (varying image
    // height, padding + title size by variant).
    const pad = variant === "library" ? "p-5" : "p-4";
    const titleSize = variant === "library" ? "text-lg" : variant === "feature" ? "text-base" : "text-[15px]";
    return (
        <button
            onClick={onClick}
            className="group flex flex-col overflow-hidden rounded-[10px] border border-gray-200 bg-white text-left shadow-sm transition duration-200 hover:-translate-y-1 hover:border-gray-300 hover:shadow-xl"
        >
            {image}
            <div className={`flex flex-1 flex-col ${pad}`}>
                <h3 className={`line-clamp-1 font-bold text-gray-900 ${titleSize}`}>{c.name}</h3>
                {c.short_description && variant !== "compact" && (
                    <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-gray-500">{c.short_description}</p>
                )}
                <div className="mt-auto pt-4">{progress}</div>
            </div>
        </button>
    );
}
